import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  Warning as WarningIcon,
  FileDownload as ExportIcon,
  Alarm as ReminderIcon,
  CheckCircle as DoneIcon,
} from "@mui/icons-material";
import { useLocalKeys } from "@hooks/useLocalKeys";
import { useNotification } from "@context/NotificationContext";
import { routes } from "@/constants/routes";

export default function SetupKeys() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { exportBundle, keyCount } = useLocalKeys();

  const [exported, setExported] = useState(false);
  const [reminderSet, setReminderSet] = useState(false);

  const handleExport = useCallback(async () => {
    try {
      const json = await exportBundle();
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `securevault-recovery-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setExported(true);
      notify(t("setupKeys.exported", "Recovery package downloaded"), "success");
    } catch {
      notify(t("setupKeys.exportError", "Export failed"), "error");
    }
  }, [exportBundle, notify, t]);

  const handleFinish = useCallback(() => {
    localStorage.setItem("setup_complete", "true");
    if (reminderSet) {
      localStorage.setItem(
        "key_export_reminder",
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      );
    }
    navigate(routes.dashboard, { replace: true });
  }, [navigate, reminderSet]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: 600,
        }}
      >
        {t("setupKeys.title", "Key Setup")}
      </Typography>
      {/* Critical warning */}
      <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
          }}
        >
          {t("setupKeys.criticalTitle", "Critical: Key Loss Warning")}
        </Typography>
        <Typography variant="body2">
          {t(
            "setupKeys.criticalMsg",
            "If you lose your encryption keys, your images CANNOT be recovered. Not by you, not by us, not by anyone. There is no master key or backdoor. Export your recovery package and store it safely.",
          )}
        </Typography>
      </Alert>
      {/* Export recovery */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("setupKeys.exportRecovery", "Export Recovery Package")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            {t(
              "setupKeys.exportDesc",
              "Download a JSON file containing all your encryption keys. Store this file in a safe location (e.g., password manager, USB drive).",
            )}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              disabled={keyCount === 0 && !exported}
            >
              {exported
                ? t("setupKeys.downloadAgain", "Download Again")
                : t("setupKeys.download", "Download Recovery Package")}
            </Button>
            {exported && <DoneIcon color="success" />}
          </Box>

          {keyCount === 0 && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                mt: 1,
                display: "block",
              }}
            >
              {t(
                "setupKeys.noKeysYet",
                "No keys to export yet. You can export after uploading your first image.",
              )}
            </Typography>
          )}
        </CardContent>
      </Card>
      {/* Reminder */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <ReminderIcon color="primary" />
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
              }}
            >
              {t("setupKeys.reminder", "Set Export Reminder")}
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={reminderSet}
                onChange={(e) => setReminderSet(e.target.checked)}
              />
            }
            label={t(
              "setupKeys.reminderLabel",
              "Remind me to export keys weekly",
            )}
          />
        </CardContent>
      </Card>
      {/* Finish */}
      <Button variant="contained" size="large" fullWidth onClick={handleFinish}>
        {t("setupKeys.finish", "Finish Setup")}
      </Button>
    </Box>
  );
}
